import { Activity, GitBranch, CalendarClock, ScanLine } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

export const Intelligence = () => {
  const ref = useReveal();
  return (
    <section
      id="intelligence"
      ref={ref}
      data-testid="intelligence-section"
      className="dark-section relative py-24 lg:py-32 bg-[#0A0A0A] text-white overflow-hidden"
    >
      <div className="grain absolute inset-0 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6 mb-14 lg:mb-20">
          <div className="col-span-12 lg:col-span-3 reveal">
            <div className="overline text-white/55">04 — Intelligence</div>
          </div>
          <div className="col-span-12 lg:col-span-9 reveal">
            <h2
              data-testid="intelligence-heading"
              className="font-display text-3xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.02] text-balance"
            >
              People on site.
              <br />
              <span className="text-white/55 italic font-normal">AI in the engine room.</span>
            </h2>
            <p className="mt-6 max-w-2xl text-white/65 leading-relaxed">
              Interiors aren't built by software — they're built by people with
              decades on the tools. AI runs underneath: modelling
              programmes, forecasting resources, flagging change impact and
              synthesising daily site signal — so our project leads make
              faster, better calls. Tools serve the team, not the other way
              round.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-px bg-white/10 reveal">
          <Tile
            testid="intel-forecasting"
            icon={CalendarClock}
            title="Forecasting"
            body="Programme and resource projections updated continuously against live site data."
            metric="Continuous"
            metricLabel="Re-baselined"
          />
          <Tile
            testid="intel-coordination"
            icon={GitBranch}
            title="Coordination"
            body="Trade dependencies resolved before they reach the wall — clashes flagged early."
            metric="Cross-trade"
            metricLabel="Clash review"
          />
          <Tile
            testid="intel-site-signal"
            icon={Activity}
            title="Site signal"
            body="Daily progress, RFIs and snag activity ingested and surfaced where decisions are made."
            metric="Daily"
            metricLabel="Synthesis"
          />
          <Tile
            testid="intel-change"
            icon={ScanLine}
            title="Change impact"
            body="Variations modelled against cost, programme and resource in seconds, not weeks."
            metric="Live"
            metricLabel="Impact model"
          />
        </div>

        <div className="mt-12 grid grid-cols-12 gap-6 reveal">
          <div className="col-span-12 lg:col-span-8 border border-white/15 p-7 lg:p-9">
            <div className="overline text-white/55">A note on the people</div>
            <p className="mt-4 text-lg lg:text-xl text-white/85 leading-snug font-display">
              "AI doesn't swing a hammer or read a room. Our team does. The
              engine just makes sure they're never short of the right
              information at the right time."
            </p>
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-col justify-between text-xs tracking-[0.22em] uppercase text-white/55 gap-3">
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 live-dot" /> Live engine
            </span>
            <span>Human-led decisions</span>
            <span>Integrated end-to-end</span>
          </div>
        </div>
      </div>
    </section>
  );
};

const Tile = ({ testid, icon: Icon, title, body, metric, metricLabel }) => (
  <div
    data-testid={testid}
    className="col-span-12 sm:col-span-6 lg:col-span-3 bg-[#0A0A0A] p-7 lg:p-8 min-h-[260px] flex flex-col justify-between hover:bg-[#111] transition-colors"
  >
    <div className="flex items-center justify-between">
      <div className="w-10 h-10 inline-flex items-center justify-center border border-white/15">
        <Icon size={18} />
      </div>
      <span className="overline text-white/45">{metricLabel}</span>
    </div>
    <div>
      <div className="font-display text-2xl lg:text-3xl">{title}</div>
      <p className="text-sm text-white/65 mt-3">{body}</p>
    </div>
    <div className="font-display text-lg text-white/80">{metric}</div>
  </div>
);

export default Intelligence;
